import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { catalogApi, type CategoryNode, type ProductSummary } from "../../api/catalog";
import type { ApiError } from "../../api/client";
import Ic from "../../components/icons.jsx";
import { SalesRepPopup } from "../../components/SalesRepPopup";
import { Select } from "../../components/Select";
import { ProductCard, attr } from "./ProductCard";
import { useCatalogOrdering } from "./useCatalogOrdering";
import { matchesPriceRange, formatPrice, listCurrency, sanitizePriceInput } from "./catalogFilters";
import { stockStatus } from "../../lib/stockStatus";
import { listedProducts, packagingCodes, prettyIndustry, splitIndustry } from "./productAttrs";
import { formatNumber } from "../../lib/format";

type View = "grid" | "list";
type SortKey = "popular" | "name_asc" | "protein_asc" | "protein_desc" | "sku_asc" | "price_asc" | "price_desc";

const PAGE_SIZE = 60;

/**
 * Catalog landing — full-feature listing with comprehensive filters,
 * dynamic facet counts and instant client-side sort. The catalog fits
 * comfortably in memory; for much larger catalogs we push filtering to BE.
 */
export default function CatalogIndexPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const { canOrder, orderedIds, addToOrder, openDatasheet } = useCatalogOrdering();

  const [allTree, setAllTree] = useState<CategoryNode[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<View>("grid");
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false); // mobile: off-canvas filter sheet

  // Filter state
  const [search, setSearch] = useState("");
  const [industries, setIndustries] = useState<Set<string>>(new Set());
  const [proteinMin, setProteinMin] = useState<string>("");
  const [proteinMax, setProteinMax] = useState<string>("");
  const [priceMin, setPriceMin] = useState<string>("");
  const [priceMax, setPriceMax] = useState<string>("");
  const [stock, setStock] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortKey>("popular");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    void catalogApi
      .tree(locale)
      .then(setAllTree)
      .catch((e: ApiError) => setError(e.message));
  }, [locale]);

  // The whole catalog is loaded once, not per category: search has to reach
  // every product, and switching category must not cost a round-trip. Category
  // then becomes a client-side filter like the rest. An ERP article that is a
  // packaging of a catalog product stays out: it is ordered from that product's page.
  useEffect(() => {
    setLoading(true);
    void catalogApi
      .listProducts({ size: 2000, locale })
      .then((p) => {
        setProducts(listedProducts(p.content));
        setProductsLoaded(true);
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [locale]);

  // Lines (top categories) holding a listed product, with their counts. A line of
  // packagings only would be empty, so it is not offered.
  const tree = useMemo(() => {
    if (!allTree || !productsLoaded) return null;
    return allTree.filter((root) => {
      const ids = subtreeIds(root);
      return products.some((p) => p.categoryId && ids.has(p.categoryId));
    });
  }, [allTree, products, productsLoaded]);
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const root of tree ?? []) {
      const ids = subtreeIds(root);
      out[root.id] = products.filter((p) => p.categoryId && ids.has(p.categoryId)).length;
    }
    return out;
  }, [tree, products]);

  useEffect(() => {
    if (tree && tree.length > 0 && !tree.some((c) => c.id === activeId)) setActiveId(tree[0].id);
  }, [tree, activeId]);

  // Reset filters whenever the user switches category — different category → different facet space.
  useEffect(() => {
    setIndustries(new Set());
    setProteinMin("");
    setProteinMax("");
    setPriceMin("");
    setPriceMax("");
    setStock(new Set());
    setVisibleCount(PAGE_SIZE);
  }, [activeId]);

  // Reset paging when the visible result set changes due to filters/sort.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, industries, proteinMin, proteinMax, priceMin, priceMax, stock, sort]);

  // Build a flat map of categoryId → image URL so we can render category banner per product card.
  const imageByCategory = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: CategoryNode[]) => {
      for (const n of nodes) {
        if (n.imageUrl) map.set(n.id, n.imageUrl);
        if (n.children) walk(n.children);
      }
    };
    if (tree) walk(tree);
    return map;
  }, [tree]);

  const activeRoot = useMemo(() => tree?.find((c) => c.id === activeId) ?? null, [tree, activeId]);

  // Products of the active category — the default working set.
  const inCategory = useMemo(() => {
    if (!activeId || !tree) return products;
    const root = tree.find((c) => c.id === activeId);
    if (!root) return products;
    const ids = subtreeIds(root);
    return products.filter((p) => p.categoryId && ids.has(p.categoryId));
  }, [products, activeId, tree]);

  // A search reaches the WHOLE catalog: the user is looking for a product, not
  // for a product inside the category that happens to be open.
  const searching = search.trim().length > 0;
  const scope = searching ? products : inCategory;

  const facets = useMemo(() => buildFacets(scope), [scope]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = scope.filter((p) => {
      if (q) {
        // A packaging's ERP code finds its product too.
        const hay = `${p.sku} ${p.name ?? ""} ${packagingCodes(p).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (industries.size > 0) {
        const prodIndustries = splitIndustry(attr(p, "industry"));
        if (![...industries].some((i) => prodIndustries.includes(i))) return false;
      }
      const protein = numAttr(p, "protein");
      if (proteinMin !== "" && (protein == null || protein < Number(proteinMin))) return false;
      if (proteinMax !== "" && (protein == null || protein > Number(proteinMax))) return false;
      if (!matchesPriceRange(p.listPrice, priceMin, priceMax)) return false;
      // A product of unknown stock is neither in nor out, so an availability filter leaves it out.
      if (stock.size > 0) {
        const s = stockStatus(p.sku);
        if (s === "UNKNOWN" || !stock.has(s === "IN" ? "in" : "out")) return false;
      }
      return true;
    });
    out = sortProducts(out, sort);
    return out;
  }, [scope, search, industries, proteinMin, proteinMax, priceMin, priceMax, stock, sort]);

  // The whole catalog is loaded, so count the products themselves: a sum of the
  // per-category counts would count a product filed under two categories twice.
  const totalAcrossLines = products.length;
  const activeFilterChips = buildChipList({
    search, industries, proteinMin, proteinMax, priceMin, priceMax, stock,
  }, t, facets.currency);
  const clearAll = () => {
    setSearch(""); setIndustries(new Set());
    setProteinMin(""); setProteinMax("");
    setPriceMin(""); setPriceMax(""); setStock(new Set());
  };

  return (
    <div className="fadein">
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("catalog.breadcrumb.aria", "Breadcrumb")}>
            <span style={crumbMuted}>{t("catalog.breadcrumb.products", "Products")}</span>
            <span><Link to="/catalog" style={crumbLink}>{t("catalog.breadcrumb.catalog", "Catalog")}</Link></span>
            {activeRoot && <span style={crumbCurrent}>{activeRoot.name}</span>}
          </nav>
          <h1>{t("catalog.title", "Catalog")}</h1>
          <div className="sub">
            {tree
              ? t(
                  "catalog.subtitle",
                  "{{skus}} active SKUs across {{families}} product families. Specifications and supporting documentation available for every product.",
                  { skus: formatNumber(totalAcrossLines), families: tree.length },
                )
              : t("catalog.loadingCatalog", "Loading catalog…")}
          </div>
        </div>
      </div>

      {error && <div style={errorBox}>{error}</div>}

      <div className={`catalog-layout${filtersOpen ? " filters-open" : ""}`}>
        <aside className="cat-side">
          <button type="button" className="btn primary cat-filters-close" onClick={() => setFiltersOpen(false)}>
            {t("catalog.filter.done", "Done")}
          </button>
          <div className="group">
            <h4>{t("catalog.filter.categories", "Categories")}</h4>
            <div className="cat-list">
              {tree?.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  className={activeId === c.id ? "active" : ""}
                  onClick={() => setActiveId(c.id)}
                  style={{ cursor: "pointer" }}
                >
                  <span>{c.name}</span>
                  <span className="n">{counts[c.id] ?? "…"}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="group">
            <h4>{t("catalog.filter.search", "Search")}</h4>
            <input
              className="input"
              placeholder={t("catalog.filter.searchPlaceholder", "SKU, name, type…")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {facets.industry.length > 1 && (
            <FilterGroup title={t("attr.industry", "Industry")} countSelected={industries.size}>
              {facets.industry.map((s) => (
                <Check key={s.value} checked={industries.has(s.value)} onChange={() => toggle(setIndustries, s.value)}>
                  <span>{prettyIndustry(s.value, t)}</span>
                  <span className="muted">{s.count}</span>
                </Check>
              ))}
            </FilterGroup>
          )}

          {/* A premix or a feed carries no protein value, so their lines get no protein filter. */}
          {facets.proteinRange.max > 0 && (
            <FilterGroup title={t("catalog.filter.proteinRange", "Protein range (%)")} countSelected={(proteinMin || proteinMax) ? 1 : 0}>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="input" placeholder={t("catalog.filter.proteinMin", "min")} value={proteinMin}
                  onChange={(e) => setProteinMin(e.target.value.replace(/[^0-9]/g, ""))} />
                <input className="input" placeholder={t("catalog.filter.proteinMax", "max")} value={proteinMax}
                  onChange={(e) => setProteinMax(e.target.value.replace(/[^0-9]/g, ""))} />
              </div>
              <span className="muted" style={{ fontSize: 11 }}>
                {t("catalog.filter.proteinAvailable", "Available: {{min}}–{{max}}%", { min: facets.proteinRange.min, max: facets.proteinRange.max })}
              </span>
            </FilterGroup>
          )}

          <FilterGroup title={t("catalog.filter.priceRange", "Price range")} countSelected={(priceMin || priceMax) ? 1 : 0}>
            <div style={{ display: "flex", gap: 6 }}>
              <input className="input" inputMode="decimal" placeholder={t("catalog.filter.priceMin", "min")} value={priceMin}
                onChange={(e) => setPriceMin(sanitizePriceInput(e.target.value))} />
              <input className="input" inputMode="decimal" placeholder={t("catalog.filter.priceMax", "max")} value={priceMax}
                onChange={(e) => setPriceMax(sanitizePriceInput(e.target.value))} />
            </div>
            {facets.priceRange.max > 0 && (
              <span className="muted" style={{ fontSize: 11 }}>
                {t("catalog.filter.priceAvailable", "Available: {{min}}–{{max}}", {
                  min: formatPrice(facets.priceRange.min, facets.currency, 0),
                  max: formatPrice(facets.priceRange.max, facets.currency, 0),
                })}
              </span>
            )}
          </FilterGroup>

          {(facets.stock.in > 0 || facets.stock.out > 0) && (
            <FilterGroup title={t("catalog.filter.availability", "Availability")} countSelected={stock.size}>
              <Check checked={stock.has("in")} onChange={() => toggle(setStock, "in")}>
                <span>{t("catalog.inStock", "In stock")}</span>
                <span className="muted">{facets.stock.in}</span>
              </Check>
              <Check checked={stock.has("out")} onChange={() => toggle(setStock, "out")}>
                <span>{t("catalog.outOfStock", "Out of stock")}</span>
                <span className="muted">{facets.stock.out}</span>
              </Check>
            </FilterGroup>
          )}

          {activeFilterChips.length > 0 && (
            <button type="button" className="btn sm" onClick={clearAll} style={{ width: "100%" }}>
              {t("catalog.button.clearAllFilters", "Clear all filters")}
            </button>
          )}
        </aside>

        <div>
          <div className="cat-toolbar">
            <div className="left" style={{ flexWrap: "wrap" }}>
              <button type="button" className="btn cat-filters-btn" onClick={() => setFiltersOpen(true)}>
                {t("catalog.filter.button", "Filters")}
              </button>
              <span className="muted" style={{ fontSize: 12.5 }}>
                <b style={{ color: "var(--ink)" }}>{filtered.length}</b>
                {filtered.length !== scope.length
                  ? t("catalog.products.filteredOf", " of {{total}} products", { total: scope.length })
                  : t("catalog.products.count", " products")}
                {/* While searching the scope is the whole catalog, so naming the
                    open category here would be a lie. */}
                {searching
                  ? t("catalog.products.wholeCatalog", " · whole catalog")
                  : activeRoot ? t("catalog.products.category", " · {{cat}}", { cat: activeRoot.name }) : ""}
              </span>
              {activeFilterChips.map((chip) => (
                <span key={chip.key} className="chip">
                  {chip.label}{" "}
                  <button type="button" onClick={chip.clear} style={chipX} aria-label={t("catalog.removeFilter", "Remove {{label}} filter", { label: chip.label })}><Ic.x /></button>
                </span>
              ))}
            </div>
            <div className="right">
              {/* .cat-sort carries the mobile full-width rule, so it stays on a wrapper. */}
              <div className="cat-sort" style={{ width: 200 }}>
                <Select<SortKey>
                  value={sort}
                  onChange={setSort}
                  options={[
                    { value: "popular", label: t("catalog.sort.popular", "Sort: Popular") },
                    { value: "name_asc", label: t("catalog.sort.nameAsc", "Sort: Name A→Z") },
                    { value: "protein_asc", label: t("catalog.sort.proteinAsc", "Sort: Protein ↑") },
                    { value: "protein_desc", label: t("catalog.sort.proteinDesc", "Sort: Protein ↓") },
                    { value: "sku_asc", label: t("catalog.sort.skuAsc", "Sort: SKU ↑") },
                    { value: "price_asc", label: t("catalog.sort.priceAsc", "Sort: Price ↑") },
                    { value: "price_desc", label: t("catalog.sort.priceDesc", "Sort: Price ↓") },
                  ]}
                />
              </div>
              <div className="view-switch">
                <button type="button" className={view === "grid" ? "active" : ""} onClick={() => setView("grid")}>
                  <Ic.grid />
                </button>
                <button type="button" className={view === "list" ? "active" : ""} onClick={() => setView("list")}>
                  <Ic.list />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <p style={{ color: "var(--ink-3)", padding: 20 }}>{t("catalog.loading", "Loading…")}</p>
          ) : view === "grid" ? (
            <>
              <div className="cat-grid">
                {filtered.slice(0, visibleCount).map((p) => (
                  <ProductCard
                    key={p.id}
                    p={p}
                    imageUrl={p.categoryId ? imageByCategory.get(p.categoryId) ?? null : null}
                    onAddToOrder={() => addToOrder(p)}
                    onDatasheet={() => openDatasheet(p)}
                    canOrder={canOrder}
                    ordered={orderedIds.has(p.id)}
                  />
                ))}
                {filtered.length === 0 && (
                  <div className="empty">
                    {t("catalog.empty.noMatches", "No matches.")} <button type="button" onClick={clearAll} className="btn sm">{t("catalog.button.clearFilters", "Clear filters")}</button>
                  </div>
                )}
              </div>
              {visibleCount < filtered.length && (
                <div className="load-more-row" style={loadMoreRow}>
                  <span className="muted" style={{ fontSize: 12.5 }}>
                    {t("catalog.showing", "Showing {{shown}} of {{total}}", { shown: visibleCount, total: filtered.length })}
                  </span>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setVisibleCount((n) => Math.min(n + PAGE_SIZE, filtered.length))}
                  >
                    {t("catalog.loadMore", "Load {{count}} more", { count: Math.min(PAGE_SIZE, filtered.length - visibleCount) })}
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => setVisibleCount(filtered.length)}
                  >
                    {t("catalog.loadAll", "Load all ({{count}})", { count: filtered.length })}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="card" style={{ overflow: "hidden" }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t("catalog.table.skuName", "SKU / Product")}</th>
                    <th className="num">{t("attr.protein", "Protein")}</th>
                    <th className="num">{t("attr.fat", "Fat")}</th>
                    <th>{t("attr.industry", "Industry")}</th>
                    <th className="num">{t("catalog.price", "Price")}</th>
                    <th className="num">{t("common.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, visibleCount).map((p) => (
                    <tr key={p.id} onClick={() => goTo(p)} style={{ cursor: p.categorySlug ? "pointer" : "default" }}>
                      <td>
                        <div className="mono" style={{ fontSize: 11, color: "var(--ink-4)" }}>{p.sku}</div>
                        <b>{p.name}</b>
                      </td>
                      <td className="num">{attr(p, "protein")}</td>
                      <td className="num">{attr(p, "fat")}</td>
                      <td>{splitIndustry(attr(p, "industry")).map((v) => prettyIndustry(v, t)).join(", ")}</td>
                      <td className="num">{p.listPrice != null ? formatPrice(p.listPrice, p.currency) : "—"}</td>
                      <td><button type="button" className="btn sm" onClick={(e) => e.stopPropagation()}>{t("catalog.button.datasheet", "Datasheet")}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {visibleCount < filtered.length && (
                <div className="load-more-row" style={loadMoreRow}>
                  <span className="muted" style={{ fontSize: 12.5 }}>
                    {t("catalog.showing", "Showing {{shown}} of {{total}}", { shown: visibleCount, total: filtered.length })}
                  </span>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setVisibleCount((n) => Math.min(n + PAGE_SIZE, filtered.length))}
                  >
                    {t("catalog.loadMore", "Load {{count}} more", { count: Math.min(PAGE_SIZE, filtered.length - visibleCount) })}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SalesRepPopup />
    </div>
  );
}

function goTo(p: ProductSummary) {
  if (p.categorySlug) {
    window.location.href = `/catalog/${p.categorySlug}/${encodeURIComponent(p.sku)}`;
  }
}

// ---- helpers --------------------------------------------------------------

function FilterGroup({ title, countSelected, children }: { title: string; countSelected: number; children: React.ReactNode }) {
  return (
    <div className="group">
      <h4>{title}{countSelected > 0 ? ` · ${countSelected}` : ""}</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{children}</div>
    </div>
  );
}

function Check({ checked, onChange, children }: { checked: boolean; onChange: () => void; children: React.ReactNode }) {
  return (
    <label style={checkRow}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span style={{ display: "flex", justifyContent: "space-between", flex: 1, gap: 8 }}>{children}</span>
    </label>
  );
}

function toggle(setter: React.Dispatch<React.SetStateAction<Set<string>>>, value: string) {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  });
}

/** A category and all categories under it. */
function subtreeIds(root: CategoryNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: CategoryNode) => { ids.add(n.id); n.children?.forEach(walk); };
  walk(root);
  return ids;
}

function numAttr(p: ProductSummary, key: string): number | null {
  const raw = attr(p, key).replace(",", ".");
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

function buildFacets(items: ProductSummary[]) {
  // Industry facet: each product can carry several comma-joined industry slugs,
  // so tally per-slug across all of a product's industries (unlike the single-value tally above).
  const industryTally = () => {
    const map = new Map<string, number>();
    for (const p of items) {
      for (const slug of splitIndustry(attr(p, "industry"))) {
        map.set(slug, (map.get(slug) ?? 0) + 1);
      }
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count }));
  };
  const proteins = items.map((p) => numAttr(p, "protein")).filter((x): x is number => x != null);
  const prices = items.map((p) => p.listPrice).filter((x): x is number => x != null);
  let inStockCount = 0;
  let outStockCount = 0;
  // Only a known stock counts. Unknown is not out of stock, so with no stock source both counts
  // stay zero and the availability filter is not drawn at all.
  for (const p of items) {
    const s = stockStatus(p.sku);
    if (s === "IN") inStockCount++;
    else if (s === "OUT") outStockCount++;
  }
  return {
    industry: industryTally(),
    proteinRange: { min: proteins.length ? Math.min(...proteins) : 0, max: proteins.length ? Math.max(...proteins) : 0 },
    priceRange: { min: prices.length ? Math.min(...prices) : 0, max: prices.length ? Math.max(...prices) : 0 },
    currency: listCurrency(items),
    stock: { in: inStockCount, out: outStockCount },
  };
}

function sortProducts(items: ProductSummary[], key: SortKey): ProductSummary[] {
  const arr = [...items];
  switch (key) {
    case "name_asc":
      return arr.sort((a, b) => (a.name).localeCompare(b.name));
    case "sku_asc":
      return arr.sort((a, b) => a.sku.localeCompare(b.sku));
    case "protein_asc":
      return arr.sort((a, b) => (numAttr(a, "protein") ?? Infinity) - (numAttr(b, "protein") ?? Infinity));
    case "protein_desc":
      return arr.sort((a, b) => (numAttr(b, "protein") ?? -Infinity) - (numAttr(a, "protein") ?? -Infinity));
    case "price_asc":
      return arr.sort((a, b) => (a.listPrice ?? Infinity) - (b.listPrice ?? Infinity));
    case "price_desc":
      return arr.sort((a, b) => (b.listPrice ?? -Infinity) - (a.listPrice ?? -Infinity));
    default:
      return arr;
  }
}

type ChipSpec = { key: string; label: string; clear: () => void };
function buildChipList(s: {
  search: string;
  industries: Set<string>;
  proteinMin: string;
  proteinMax: string;
  priceMin: string;
  priceMax: string;
  stock: Set<string>;
}, t: TFunction, currency: string): ChipSpec[] {
  const chips: ChipSpec[] = [];
  if (s.search) chips.push({ key: "q", label: `“${s.search}”`, clear: () => {/* handled at parent */} });
  s.industries.forEach((v) => chips.push({ key: `ind-${v}`, label: prettyIndustry(v, t), clear: () => {} }));
  if (s.proteinMin || s.proteinMax)
    chips.push({ key: "p", label: t("catalog.chip.protein", "Protein {{min}}–{{max}}%", { min: s.proteinMin || "0", max: s.proteinMax || "∞" }), clear: () => {} });
  if (s.priceMin || s.priceMax) {
    const bound = (v: string, fallback: string) => {
      const n = Number(v);
      return v && Number.isFinite(n) ? formatPrice(n, currency, 0) : fallback;
    };
    chips.push({
      key: "price",
      label: t("catalog.chip.price", "Price {{min}}–{{max}}", { min: bound(s.priceMin, "0"), max: bound(s.priceMax, "∞") }),
      clear: () => {},
    });
  }
  s.stock.forEach((v) => chips.push({
    key: `stock-${v}`,
    label: v === "in" ? t("catalog.inStock", "In stock") : t("catalog.outOfStock", "Out of stock"),
    clear: () => {},
  }));
  return chips;
}

const errorBox: React.CSSProperties = {
  padding: 10,
  background: "var(--err-bg)",
  color: "var(--err)",
  borderRadius: 6,
  fontSize: 13,
};
const chipX: React.CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  marginLeft: 4,
  display: "inline-flex",
  alignItems: "center",
};
const checkRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12.5,
  cursor: "pointer",
};
const crumbMuted: React.CSSProperties = { color: "var(--ink-4)" };
const crumbLink: React.CSSProperties = { color: "var(--ink-3)", textDecoration: "none" };
const crumbCurrent: React.CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
const loadMoreRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  padding: 18,
  marginTop: 12,
};
